import { memo, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useOverlays } from "contexts/OverlayContext";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { PlusIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { PlainModal } from "@design-system/Modal/Modal";
import { useDeleteDocumentCategoryMutation } from "@api/documentCategory.api";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { UpdateDocumentCategoryModal } from "../UpdateDocumentCategoryModal";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    DocumentCategoryFlattenedItemType,
    DocumentCategoryRow,
} from "../utils";

export const ActionsCell = memo(function ActionsCell({
    row,
}: CellContext<ViewTableFeatures, DocumentCategoryRow, unknown>) {
    const item = row.original;

    if (item.type === DocumentCategoryFlattenedItemType.CustomCategory) {
        return <CustomCategoryActions item={item} />;
    }

    return <AddSubtypeIndicator item={item} />;
});

// ─── Custom category: update + delete ────────────────────────────────────────

const CustomCategoryActions = ({
    item,
}: Readonly<{ item: DocumentCategoryRow }>) => {
    const deleteCategory = useDeleteDocumentCategoryMutation(item.category);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);

    return (
        <ViewTableCell captureClicks>
            <Menu.More className={viewTableStyles.showOnHover}>
                <Menu.Item onClick={() => setShowUpdateModal(true)}>
                    Update
                </Menu.Item>
                <Menu.Item onClick={() => setShowDeleteModal(true)} destructive>
                    Delete
                </Menu.Item>
            </Menu.More>

            {showUpdateModal && (
                <PlainModal
                    show={showUpdateModal}
                    onHide={() => setShowUpdateModal(false)}
                >
                    <UpdateDocumentCategoryModal
                        category={item.category}
                        onHide={() => setShowUpdateModal(false)}
                    />
                </PlainModal>
            )}

            {showDeleteModal && (
                <ConfirmationModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    title={`Are you sure that you want to delete subtype: ${item.label}?`}
                    question=""
                    no="Cancel"
                    onReject={() => setShowDeleteModal(false)}
                    onConfirmMutation={deleteCategory}
                    yes="Delete"
                />
            )}
        </ViewTableCell>
    );
};

// ─── Non-custom category: add subtype button ──────────────────────────────────

const AddSubtypeIndicator = ({
    item,
}: Readonly<{ item: DocumentCategoryRow }>) => {
    const { openOverlay } = useOverlays();

    if (item.parentId !== null) {
        return null;
    }

    return (
        <ViewTableCell captureClicks>
            <Tooltip content="Add subtype">
                <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    aria-label="Add subtype"
                    onClick={(e) => {
                        e.stopPropagation();
                        openOverlay("createDocumentCategory", {
                            parentCategoryId: item.id,
                        });
                    }}
                >
                    <PlusIcon />
                </Button>
            </Tooltip>
        </ViewTableCell>
    );
};
